// Sharing creatives for verified external approval, and the record that comes
// back. Lives here rather than in a screen because two surfaces need it: the
// campaign wizard's Preview/Approve step, and the Creative Library drawer for a
// creative that already exists — a link is far more often wanted after the fact
// than at creation time.
//
// One review link names one creative and lives 72 hours from its creation. Any
// number of people may open it; each confirms their own email. There is no
// revoke: a link ends at its expiry. While a creative has a live link at its
// current content, asking again hands that same link back, so the modal shows
// one row per creative — the live link to copy, or "Create link" — and nothing
// for links that have expired.
//
// Meta and Display creatives can be shared; CTV cannot. The mint endpoint
// checks each creative (`require_reviewable`): a Meta one needs its official
// previews rendered, so an unrendered one is refused rather than producing a
// link to a page of empty frames; a Display one needs at least one banner The
// Trade Desk can take, and the reviewer sees the banners themselves.

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { useCreateCreativeReviewPackage, useCreativeReviewPackages } from '@/api/manage-hooks';
import { creativeReviewPackages } from '@/api/resources';
import type {
  CreativeReviewLink,
  CreativeReviewPackageSummary,
  CreativeReviewerActivity,
} from '@/api/types.gen';
import { ago, fmtDateTime } from '@/lib/datetime';

interface ShareAgencyModalProps {
  open: boolean;
  onClose: () => void;
  creativeIds?: string[];
  production?: { title?: string | null } | null;
  /** Display names by creative id, when the caller already has them. */
  creativeNames?: Record<string, string>;
  /** What the link shows: Meta's official previews, or Display banners. */
  channel?: 'meta' | 'dsp';
}

/** The link a row offers: still live by the server's word and by this clock. */
interface LiveLink {
  share_url: string;
  expires_at: string;
  reviewers: CreativeReviewerActivity[];
}

function hoursLeft(expiresAt: string, now: number): string {
  const hours = Math.floor((Date.parse(expiresAt) - now) / 3_600_000);
  return hours < 1 ? 'expires in under 1 h' : `expires in ${hours} h`;
}

/**
 * The newest live, copyable link covering this creative at its current
 * content. A link minted before links were derived has no `share_url` and
 * cannot be copied again, and one whose creative has changed since refuses
 * every decision, so neither is offered; a link past its expiry is never shown,
 * even if the list was read before it expired.
 */
export function liveLinkFor(
  creativeId: string,
  packages: readonly CreativeReviewPackageSummary[] | undefined,
  minted: CreativeReviewLink | undefined,
  now: number,
): LiveLink | null {
  const listed = (packages ?? [])
    .filter((pkg) => pkg.live && pkg.current && pkg.share_url && pkg.creative_ids.includes(creativeId))
    .filter((pkg) => Date.parse(pkg.expires_at) > now)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
  if (listed?.share_url) {
    return { share_url: listed.share_url, expires_at: listed.expires_at, reviewers: listed.reviewers };
  }
  // Just minted, and the list has not caught up yet. Once the list knows the
  // package it is the authority: a link it no longer offers (the creative has
  // changed since, Astra A3 2026-10-08) refuses every decision, so the copy
  // this modal remembers from minting must not bring it back.
  const listedMinted = (packages ?? []).some((pkg) => pkg.id === minted?.package_id);
  if (minted && !listedMinted && Date.parse(minted.expires_at) > now) {
    return { share_url: minted.share_url, expires_at: minted.expires_at, reviewers: [] };
  }
  return null;
}

function ShareAgencyModal({
  open,
  onClose,
  creativeIds = [],
  production,
  creativeNames = {},
  channel = 'meta',
}: ShareAgencyModalProps) {
  const queryClient = useQueryClient();
  const createLinks = useCreateCreativeReviewPackage();
  const [minted, setMinted] = useState<Record<string, CreativeReviewLink>>({});
  const [copied, setCopied] = useState<string | null>(null);
  // The list is revision-controlled and reviewer activity bumps no revision,
  // so who opened a link would otherwise stay as it was when first read.
  const lists = useQueries({
    queries: creativeIds.map((id) => creativeReviewPackages(id, { enabled: open })),
  });
  const signature = creativeIds.join('|');
  useEffect(() => {
    if (!open) return;
    for (const id of signature.split('|').filter(Boolean)) {
      void queryClient.refetchQueries(
        { queryKey: creativeReviewPackages.key(id), exact: true },
        { cancelRefetch: false },
      );
    }
  }, [open, signature, queryClient]);
  if (!open) return null;

  const shown = channel === 'dsp' ? 'Display banners' : 'Meta previews';
  const requestName = production?.title ? `${production.title} — ${shown}` : shown;
  const now = Date.now();
  const rows = creativeIds.map((id, index) => {
    const packages = lists[index]?.data;
    const link = liveLinkFor(id, packages, minted[id], now);
    // A link is named "<request name> — <creative name>"; that is the name
    // to fall back on when the caller did not pass one.
    const named = packages?.find((pkg) => pkg.creative_ids.length === 1)?.name;
    const name = creativeNames[id]
      ?? minted[id]?.creative_name
      ?? (named?.startsWith(`${requestName} — `) ? named.slice(requestName.length + 3) : named)
      ?? `Creative ${index + 1}`;
    return { id, name, link, loading: !!lists[index]?.isPending };
  });
  const missing = rows.filter((row) => !row.link && !row.loading).map((row) => row.id);

  const create = (ids: string[]) => {
    if (!ids.length || createLinks.isPending) return;
    void createLinks
      .mutateAsync({ name: requestName, creative_ids: ids })
      .then((links) => {
        setMinted((current) => {
          const next = { ...current };
          for (const link of links) next[link.creative_id] = link;
          return next;
        });
      })
      .catch(() => {});
  };

  const copy = (url: string, id: string) => {
    void navigator.clipboard?.writeText(url).then(() => setCopied(id)).catch(() => {});
  };

  return (
    <ResponsiveOverlay label="creative review share" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:640}}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">
              {channel === 'dsp' ? 'Share Display banners for approval' : 'Share official Meta previews'}
            </div>
            <div className="muted" style={{fontSize:11.5,marginTop:2}}>
              One link per creative, for {production?.title || 'this campaign'} · each expires 72 hours after it is created.
            </div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {createLinks.isError && (
            <div className="asset-err" style={{marginBottom:10}}>
              <span>⚠</span>
              <span>
                {createLinks.error?.message
                  || (channel === 'dsp'
                    ? 'A review link could not be created. The creative needs at least one banner The Trade Desk can take.'
                    : 'A review link could not be created. Previews must be generated and current first.')}
              </span>
            </div>
          )}

          {rows.map((row) => (
            <div key={row.id} className="review-share-row" data-creative={row.id} style={{padding:'10px 0',borderBottom:'1px solid var(--line-2)'}}>
              <div className="flex gap-1" style={{alignItems:'center',justifyContent:'space-between'}}>
                <b style={{fontSize:12.5}}>{row.name}</b>
                {row.link && (
                  <span className="muted" style={{fontSize:11}}>{hoursLeft(row.link.expires_at, now)}</span>
                )}
              </div>
              {row.link ? (
                <>
                  <div className="flex gap-1" style={{marginTop:6}}>
                    <input className="input mono" readOnly aria-label={`Review link for ${row.name}`} value={row.link.share_url}/>
                    <button className="btn btn-sm" onClick={() => copy(row.link!.share_url, row.id)}>
                      {copied === row.id ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <ReviewerList reviewers={row.link.reviewers} now={now} />
                </>
              ) : row.loading ? (
                <div className="muted" style={{fontSize:11,marginTop:6}}>Checking for a live link…</div>
              ) : (
                <button
                  className="btn btn-sm btn-primary"
                  style={{marginTop:6}}
                  disabled={createLinks.isPending}
                  onClick={() => create([row.id])}
                >
                  {createLinks.isPending ? 'Creating secure link…' : 'Create link'}
                </button>
              )}
            </div>
          ))}

          {missing.length > 1 && (
            <button
              className="btn btn-primary"
              style={{marginTop:12}}
              disabled={createLinks.isPending}
              onClick={() => create(missing)}
            >
              {createLinks.isPending ? 'Creating secure links…' : `Create ${missing.length} links`}
            </button>
          )}

          <div className="muted" style={{fontSize:11,marginTop:12}}>
            Anyone you send a link to confirms their email before they see anything. Their address,
            the time and their IP are recorded against whatever they approve.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

/** Who confirmed an email on a link, and how they have used it since. */
function ReviewerList({ reviewers, now }: { reviewers: CreativeReviewerActivity[]; now: number }) {
  if (!reviewers.length) {
    return <div className="muted" style={{fontSize:11,marginTop:6}}>Nobody has opened this link yet.</div>;
  }
  return (
    <ul style={{listStyle:'none',margin:'8px 0 0',padding:0,fontSize:11.5}}>
      {reviewers.map((reviewer) => (
        <li key={reviewer.verification_id} style={{marginBottom:4}}>
          <b>{reviewer.email}</b>
          <span className="muted">
            {' · first opened '}{fmtDateTime(reviewer.verified_at)}
            {' · last opened '}{ago(reviewer.last_opened_at ?? reviewer.verified_at, now)}
            {reviewer.reopen_count > 0 && ` · email link reopened ${reviewer.reopen_count}×`}
          </span>
          {reviewer.viewed_from_other_browsers && (
            <span className="tag warn" style={{marginLeft:6}}>also viewed from another browser</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The evidentiary record for one creative: who approved, when, and from where.
 * Decisions made through links that have since expired are included.
 * `ip_address` and `user_agent` read `—` once retention has cleared them; the
 * decision itself never expires.
 */
function ExternalApprovalRecord({ creativeId }: { creativeId: string }) {
  const packages = useCreativeReviewPackages(creativeId);
  const approvals = (packages.data || [])
    .flatMap((item) => item.approvals)
    .sort((a, b) => (a.decided_at < b.decided_at ? 1 : -1));
  if (!approvals.length) return null;
  return (
    <div className="panel" style={{padding:14,marginTop:14}}>
      <div className="mono" style={{fontSize:10,letterSpacing:'.12em',color:'var(--ink-3)',textTransform:'uppercase',marginBottom:10}}>
        External approvals
      </div>
      {approvals.map((row) => (
        <div key={row.id} style={{fontSize:12,paddingBottom:8,marginBottom:8,borderBottom:'1px solid var(--line-2)'}}>
          <div>
            <span className={`tag ${row.verdict === 'changes_requested' ? 'warn' : ''}`}>
              {row.verdict === 'approved' ? 'Approved' : 'Changes requested'}
            </span>
            <b style={{marginLeft:8}}>{row.email}</b>
            {!row.current && (
              <span className="muted" style={{marginLeft:8,fontSize:11}}>
                · on an earlier revision
              </span>
            )}
          </div>
          {row.comment && <div style={{marginTop:4,color:'var(--ink-1)'}}>{row.comment}</div>}
          <div className="muted mono" style={{fontSize:10.5,marginTop:4}}>
            {new Date(row.decided_at).toISOString()} · {row.ip_address || '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

export { ShareAgencyModal, ExternalApprovalRecord };
